import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format as formatDate, isValid as isValidDate, parse as parseDate } from "date-fns";
import { Calendar, Hash, MessageSquare, Scale, TriangleAlert } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGdsOffices } from "@/features/create-claim/api/use-gds-offices";
import { OfficeCombobox, officeLabel } from "@/features/create-claim/components/office-combobox";
import { useCurrentUser } from "@/hooks/use-current-user";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { ClaimWithRelations } from "../api/use-claim";
import {
  useChangeClaimTenant,
  useClaimFamilies,
  useExtractInNewFamily,
  useFlightClaimsCount,
  useReservationItinerary,
  useSplitClaim,
  useTenants,
  useUpdateClaim,
  useUpdateClaimFamilies,
  useUpdateFlightOperatingAirline,
} from "../api/use-update-claim";
import { type ClaimTypeName, claimTypeBadgeClasses, getClaimTypeDisplayName } from "../lib/claim-types";
import { PRICE_CURRENCIES } from "../lib/money";
import { getProcedureTypeLabel, PROCEDURE_TYPE_OPTIONS, type ProcedureType } from "../lib/procedure-types";
import { AirlineCombobox } from "./airline-combobox";
import { SelectCompetentCourt } from "./states-cta/init-procedure-cta";

const CLAIM_TYPE_NAMES = Object.keys(claimTypeBadgeClasses) as ClaimTypeName[];

/** Mirrors SelectCompensationAmount.compensationOptions (Vue). */
const COMPENSATION_OPTIONS = [0, 125, 250, 200, 400, 300, 600];

interface PriceValue {
  amount: string;
  currency: string;
}

function formatLocalDate(dateString: string | Date | null | undefined): string {
  if (!dateString) {
    return "";
  }
  return formatDate(new Date(dateString), "dd/MM/yyyy HH:mm");
}

/** datetime-local value ("yyyy-MM-dd'T'HH:mm") → API schedule format ("dd/MM/yyyy HH:mm"). */
function toScheduleFormat(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  const parsed = parseDate(value, "yyyy-MM-dd'T'HH:mm", new Date());
  if (!isValidDate(parsed)) {
    return null;
  }
  return formatDate(parsed, "dd/MM/yyyy HH:mm");
}

interface ScheduleFormValues {
  scheduledDepartureLocal?: string;
  actualDepartureLocal?: string;
  scheduledArrivalLocal?: string;
  inGateArrivalLocal?: string;
}

const SCHEDULE_FIELDS: Array<{ name: keyof ScheduleFormValues; label: string; description: string }> = [
  {
    name: "scheduledDepartureLocal",
    label: "Horaire de départ prévu",
    description: "Exemple: 25/07/2023 19:00",
  },
  {
    name: "actualDepartureLocal",
    label: "Horaire de départ réel",
    description: "Exemple: 25/07/2023 23:00",
  },
  {
    name: "scheduledArrivalLocal",
    label: "Horaire d'arrivée prévu",
    description: "Exemple: 26/07/2023 07:00",
  },
  {
    name: "inGateArrivalLocal",
    label: "Horaire d'arrivée réel",
    description: "Exemple: 26/07/2023 10:30",
  },
];

interface UpdateClaimModalProps {
  claim: ClaimWithRelations;
  open: boolean;
  onClose: () => void;
}

/**
 * Port of modals/UpdateClaimModal.vue — « Modifier le dossier ».
 *
 * Remaining Vue features not yet migrated (tagged below): drag-and-drop of
 * passengers between families, effective airline search, per-passenger MC99 amounts.
 */
export function UpdateClaimModal({ claim, open, onClose }: UpdateClaimModalProps) {
  const { can } = useCurrentUser();
  const queryClient = useQueryClient();

  const isUpclaimAdmin = can(UPCLAIM_ROLES);
  const isClaimValidation = claim.claimLegalStep.name === "CLAIM_VALIDATION";
  const isCourt = claim.claimLegalStep.name === "COURT";
  const showTenantSection = isUpclaimAdmin && isClaimValidation;
  const showSplitSection = isUpclaimAdmin && isClaimValidation;

  const updateClaim = useUpdateClaim(claim.id);

  /// Procedure form (COURT)
  const [numeroRG, setNumeroRG] = useState("");
  const [juridictionId, setJuridictionId] = useState<number | null>(claim.procedure?.juridictionId ?? null);
  const [procedureType, setProcedureType] = useState<ProcedureType>(
    (claim.procedure?.procedureType as ProcedureType | undefined) ?? "PROCEDURE_CLASSIQUE",
  );
  const updateProcedure = useMutation({
    mutationFn: (payload: { numeroRG?: string; juridictionId?: number; procedureType?: ProcedureType }) =>
      notify.promise(adminAPI.putProcedure(claim.id, payload), {
        loading: "Modification de la procédure…",
        success: "Procédure modifiée avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claim.id) });
    },
  });

  /// Front conversation form
  const [rootFrontConversationId, setRootFrontConversationId] = useState("");

  /// Segment selection
  const reservationId = claim.legalSpecification.impactedSegment.reservation.id;
  const { data: itinerary } = useReservationItinerary(open ? reservationId : undefined);
  const segments = (itinerary?.segments ?? []).filter((segment) => segment.cancelledBy !== "CUSTOMER");
  const [impactedSegmentId, setImpactedSegmentId] = useState<number | null>(null);
  const [jurisdictionalSegmentId, setJurisdictionalSegmentId] = useState<number | null>(null);
  const [faultySegmentId, setFaultySegmentId] = useState<number | null>(null);

  const seenLabels = new Set<string>();
  const segmentOptions = segments
    .map((segment) => ({
      label: `${segment.flight.airline?.iata || ""}${
        segment.flight.flightIdentifier?.replace(/^[A-Z]{2}/, "") || ""
      } - ${segment.flight.departureAirport?.iata} → ${segment.flight.arrivalAirport?.iata} (${formatLocalDate(
        segment.flight.scheduledDepartureLocal,
      )})`,
      value: segment.id,
    }))
    .filter((option) => {
      if (seenLabels.has(option.label)) {
        return false;
      }
      seenLabels.add(option.label);
      return true;
    });

  /// Schedule form
  const [scheduleValues, setScheduleValues] = useState<ScheduleFormValues>({});
  const [operatingAirlineId, setOperatingAirlineId] = useState<string | null>(null);
  /// The flight this section edits — the claim's effective flight, carrying both carriers.
  const effectiveFlight = claim.legalSpecification.effectiveFlight;
  const currentOperatingAirline = effectiveFlight.operatingAirline ?? effectiveFlight.airline;
  const updateOperatingAirline = useUpdateFlightOperatingAirline(claim.id, effectiveFlight.id);
  const { data: flightClaimsCount } = useFlightClaimsCount(effectiveFlight.id, open);

  /// Legal specification form
  const [claimTypeName, setClaimTypeName] = useState<ClaimTypeName | null>(null);
  const [ec261CompensationAmount, setEc261CompensationAmount] = useState<number | null>(null);
  /// Per-passenger Convention-de-Montréal amounts — mirrors the Vue Price inputs.
  const [mc99Amounts, setMc99Amounts] = useState<Record<string, PriceValue>>({});

  /// Families
  const { data: claimFamilies } = useClaimFamilies(claim.id, open);
  const extractInNewFamily = useExtractInNewFamily(claim.id);
  const updateClaimFamilies = useUpdateClaimFamilies(claim.id);
  /// Local reassignments (userClaimId → target familyId) replacing the Vue drag-and-drop.
  const [familyOverrides, setFamilyOverrides] = useState<Record<string, string>>({});

  const baseFamilies = claimFamilies ?? [];
  const allFamilyUserClaims = baseFamilies.flatMap((family) => family.usersClaims ?? []);
  const originalFamilyByUserClaim = new Map<string, string>();
  for (const family of baseFamilies) {
    for (const userClaim of family.usersClaims ?? []) {
      originalFamilyByUserClaim.set(userClaim.id, family.id);
    }
  }
  const assignedFamilyId = (userClaimId: string) =>
    familyOverrides[userClaimId] ?? originalFamilyByUserClaim.get(userClaimId);
  const displayFamilies = baseFamilies.map((family) => ({
    ...family,
    usersClaims: allFamilyUserClaims.filter((userClaim) => assignedFamilyId(userClaim.id) === family.id),
  }));
  const hasFamilyChange = Object.entries(familyOverrides).some(
    ([userClaimId, familyId]) => originalFamilyByUserClaim.get(userClaimId) !== familyId,
  );

  /// Split claim
  const splitPassengers = (claimFamilies ?? []).flatMap((family) => family.usersClaims ?? []);
  const [selectedUserClaimIds, setSelectedUserClaimIds] = useState<string[]>([]);
  const splitClaim = useSplitClaim(claim.id);
  const canSplit = selectedUserClaimIds.length >= 1 && selectedUserClaimIds.length < splitPassengers.length;

  /// Tenant & office ID selection (TECH-1409: an agent scoped to an office ID only sees its claims)
  const { data: tenants = [] } = useTenants(open && showTenantSection);
  const currentTenantId = claim.tenantAssociation?.sourceTenantId ?? null;
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const targetTenantId = selectedTenantId ?? currentTenantId;
  const isTenantChanged = targetTenantId !== currentTenantId;
  const currentOffice = claim.legalSpecification.impactedSegment.reservation.travelAgencyOffice;
  const { data: offices = [] } = useGdsOffices(
    targetTenantId ?? undefined,
    open && showTenantSection && Boolean(targetTenantId),
  );
  const [selectedOfficeId, setSelectedOfficeId] = useState<number | null>(null);
  // A new tenant has none of the current offices: its passive office applies unless one is picked
  const targetOfficeId = selectedOfficeId ?? (isTenantChanged ? null : currentOffice.id);
  const changeClaimTenant = useChangeClaimTenant(claim.id);

  function submitProcedure() {
    updateProcedure.mutate({
      numeroRG: numeroRG || undefined,
      juridictionId: juridictionId ?? undefined,
      procedureType,
    });
  }

  function submitFrontConversation() {
    updateClaim.mutate(
      {
        payload: { legalSpecification: { rootFrontConversationId } },
        successMessage: "Conversation Front re-définie avec succès",
      },
      {
        onSuccess: () => notify.info(`Nouvel ID: ${rootFrontConversationId}`),
      },
    );
  }

  function submitSegments() {
    const legalSpecification: {
      impactedSegmentId?: number;
      jurisdictionalSegmentId?: number;
      faultySegmentId?: number;
    } = {};
    if (impactedSegmentId) {
      legalSpecification.impactedSegmentId = impactedSegmentId;
    }
    if (jurisdictionalSegmentId) {
      legalSpecification.jurisdictionalSegmentId = jurisdictionalSegmentId;
    }
    if (faultySegmentId) {
      legalSpecification.faultySegmentId = faultySegmentId;
    }
    updateClaim.mutate({
      payload: { legalSpecification },
      successMessage: "Segments mis à jour avec succès",
    });
  }

  function submitSchedule() {
    updateClaim.mutate({
      payload: {
        schedule: {
          scheduledDepartureLocal: toScheduleFormat(scheduleValues.scheduledDepartureLocal),
          actualDepartureLocal: toScheduleFormat(scheduleValues.actualDepartureLocal),
          scheduledArrivalLocal: toScheduleFormat(scheduleValues.scheduledArrivalLocal),
          inGateArrivalLocal: toScheduleFormat(scheduleValues.inGateArrivalLocal),
        },
      },
      successMessage: "Vol effectif mis à jour avec succès",
    });
  }

  function submitLegalSpecification() {
    // Only the passenger amounts actually edited are sent (Vue sparse-array behavior).
    const usersClaims = claim.usersClaims.flatMap((userClaim) => {
      const price = mc99Amounts[userClaim.id];
      if (!price) {
        return [];
      }
      const parsed = Number.parseFloat(price.amount.replace(",", "."));
      if (Number.isNaN(parsed) || parsed < 0) {
        return [];
      }
      return [
        {
          amount: Math.round(parsed * 100),
          currency: price.currency,
          precision: 2 as const,
          userClaimId: userClaim.id,
        },
      ];
    });

    updateClaim.mutate({
      payload: {
        legalSpecification: {
          claimTypeName: claimTypeName
            ? { value: claimTypeName, label: getClaimTypeDisplayName(claimTypeName, { emoji: true }) }
            : null,
          ec261CompensationAmount:
            ec261CompensationAmount !== null
              ? { value: String(ec261CompensationAmount), label: `${ec261CompensationAmount} €` }
              : null,
          usersClaims,
        },
      },
      successMessage: "Demande mise à jour, merci de rafraichir la page",
    });
  }

  /** Mirrors UpdateClaimModal.syncFamilyChanges (Vue) — sends the full reassigned structure. */
  function submitFamilyChanges() {
    if (!displayFamilies.length) {
      notify.warning("Aucune modification detectée");
      return;
    }
    updateClaimFamilies.mutate(
      displayFamilies.map((family) => ({
        familyId: family.id,
        usersClaimsIds: family.usersClaims.map((userClaim) => userClaim.id),
      })),
      { onSuccess: () => setFamilyOverrides({}) },
    );
  }

  function submitSplitClaim() {
    if (!canSplit || splitClaim.isPending) {
      return;
    }
    if (
      !window.confirm(
        `Scinder ${selectedUserClaimIds.length} passager(s) dans un nouveau dossier ? Le PNR reste identique.`,
      )
    ) {
      return;
    }
    splitClaim.mutate(selectedUserClaimIds, {
      onSuccess: () => setSelectedUserClaimIds([]),
    });
  }

  function submitTenantChange() {
    if (!targetTenantId || (!isTenantChanged && targetOfficeId === currentOffice.id)) {
      notify.warning("Veuillez sélectionner un tenant ou un office ID différent");
      return;
    }
    changeClaimTenant.mutate(
      { tenantId: targetTenantId, officeId: targetOfficeId ?? undefined },
      { onSuccess: () => setSelectedOfficeId(null) },
    );
  }

  const sectionTriggerClass =
    "flex w-full justify-between rounded-lg bg-indigo-100 px-4 py-2 text-left text-sm font-medium text-indigo-900 hover:bg-indigo-200";

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Modifier la claim</DialogTitle>
          <DialogDescription>Formulaire pour modifier le dossier en cours</DialogDescription>
        </DialogHeader>

        <DialogPanel>
          <Accordion
            defaultValue={["procedure", "front", "segments", "flight", "families", "legal"]}
            className="gap-2"
          >
            {/* NUMERO RG */}
            {isCourt ? (
              <AccordionItem value="procedure" className="border-none">
                <AccordionTrigger className={sectionTriggerClass}>Procédure contentieuse</AccordionTrigger>
                <AccordionContent className="px-4 pt-4 pb-2 text-sm text-gray-500">
                  <div className="grid grid-cols-2 gap-4 py-2">
                    <div>
                      <Label
                        htmlFor="update-numero-rg"
                        className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700"
                      >
                        <Hash className="h-4 w-4 text-gray-400" />
                        Numéro RG
                      </Label>
                      <Input
                        id="update-numero-rg"
                        value={numeroRG}
                        placeholder={claim.procedure?.identifier ?? ""}
                        onChange={(event) => setNumeroRG(event.target.value)}
                      />
                    </div>
                    <SelectCompetentCourt
                      claimId={claim.id}
                      value={juridictionId}
                      onChange={setJuridictionId}
                    />
                    <div>
                      <Label
                        htmlFor="update-procedure-type"
                        className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700"
                      >
                        <Scale className="h-4 w-4 text-gray-400" />
                        Type de procédure
                      </Label>
                      <Select
                        value={procedureType}
                        onValueChange={(value) => {
                          if (value) {
                            setProcedureType(value as ProcedureType);
                          }
                        }}
                      >
                        <SelectTrigger id="update-procedure-type" className="w-full bg-white">
                          <SelectValue>{getProcedureTypeLabel(procedureType)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {PROCEDURE_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end py-4">
                    <Button onClick={submitProcedure} disabled={updateProcedure.isPending}>
                      Modifier la procédure
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ) : null}

            {/* FRONT */}
            {!isClaimValidation ? (
              <AccordionItem value="front" className="border-none">
                <AccordionTrigger className={sectionTriggerClass}>Conversation Front</AccordionTrigger>
                <AccordionContent className="px-4 pt-4 pb-2 text-sm text-gray-500">
                  <div className="grid grid-cols-2 gap-4 py-2">
                    <div>
                      <Label
                        htmlFor="update-front-conversation"
                        className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700"
                      >
                        <MessageSquare className="h-4 w-4 text-gray-400" />
                        Front Conversation ID
                      </Label>
                      <Input
                        id="update-front-conversation"
                        value={rootFrontConversationId}
                        placeholder={claim.conversation?.frontConversationId ?? ""}
                        onChange={(event) => setRootFrontConversationId(event.target.value)}
                      />
                      <p className="mt-1 text-xs text-gray-500">Redéfinir la conversation Front principale</p>
                    </div>
                  </div>
                  <div className="flex justify-end py-4">
                    <Button onClick={submitFrontConversation} disabled={updateClaim.isPending}>
                      Redéfinir la conversation
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ) : null}

            {/* SEGMENT SELECTION */}
            <AccordionItem value="segments" className="border-none">
              <AccordionTrigger className={sectionTriggerClass}>Sélection des segments</AccordionTrigger>
              <AccordionContent className="px-4 pt-4 pb-2 text-sm text-gray-500">
                <div className="grid grid-cols-1 gap-4 py-2">
                  <div>
                    <p className="mb-2 text-sm text-gray-600">
                      Segment impacté actuel:{" "}
                      <span className="font-semibold">
                        {claim.legalSpecification.effectiveFlight?.flightIdentifier} (
                        {claim.legalSpecification.effectiveFlight?.departureAirport?.iata} →{" "}
                        {claim.legalSpecification.effectiveFlight?.arrivalAirport?.iata})
                      </span>
                    </p>
                    {segmentOptions.length > 0 ? (
                      <SegmentSelect
                        label="Segment impacté"
                        description="Le segment où l'incident s'est produit"
                        options={segmentOptions}
                        value={impactedSegmentId ?? claim.legalSpecification.impactedSegmentId ?? null}
                        onChange={setImpactedSegmentId}
                      />
                    ) : null}
                  </div>
                  <div>
                    <p className="mb-2 text-sm text-gray-600">
                      Segment juridictionnel actuel:{" "}
                      <span className="font-semibold">
                        {claim.legalSpecification.jurisdictionalSegmentId
                          ? (segmentOptions.find(
                              (option) => option.value === claim.legalSpecification.jurisdictionalSegmentId,
                            )?.label ?? "Non défini")
                          : "Non défini (utilise le segment impacté)"}
                      </span>
                    </p>
                    {segmentOptions.length > 0 ? (
                      <SegmentSelect
                        label="Segment juridictionnel"
                        description="Le segment utilisé pour déterminer la juridiction"
                        options={segmentOptions}
                        value={
                          jurisdictionalSegmentId ?? claim.legalSpecification.jurisdictionalSegmentId ?? null
                        }
                        onChange={setJurisdictionalSegmentId}
                      />
                    ) : null}
                  </div>
                  <div>
                    <p className="mb-2 text-sm text-gray-600">
                      Segment fautif actuel:{" "}
                      <span className="font-semibold">
                        {claim.legalSpecification.faultySegmentId
                          ? (segmentOptions.find(
                              (option) => option.value === claim.legalSpecification.faultySegmentId,
                            )?.label ?? "Non défini")
                          : "Non défini"}
                      </span>
                    </p>
                    {segmentOptions.length > 0 ? (
                      <SegmentSelect
                        label="Segment fautif"
                        description="Le segment à l'origine de la perturbation"
                        options={segmentOptions}
                        value={faultySegmentId ?? claim.legalSpecification.faultySegmentId ?? null}
                        onChange={setFaultySegmentId}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="flex justify-end py-4">
                  <Button onClick={submitSegments} disabled={updateClaim.isPending}>
                    Modifier les segments
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* FLIGHT */}
            <AccordionItem value="flight" className="border-none">
              <AccordionTrigger className={sectionTriggerClass}>
                Modifier le vol &amp; ses horaires
              </AccordionTrigger>
              <AccordionContent className="px-4 pt-4 pb-2 text-sm text-gray-500">
                <div>
                  <AirlineCombobox
                    label="Compagnie opératrice du vol"
                    value={operatingAirlineId}
                    onSelect={(airline) => setOperatingAirlineId(airline.id)}
                  />
                  <p className="mt-2 text-sm text-slate-600">
                    La compagnie opératrice actuelle est :{" "}
                    <span className="font-semibold text-slate-700">{currentOperatingAirline.name}</span>{" "}
                    (IATA:{" "}
                    <span className="font-semibold text-slate-700">{currentOperatingAirline.iata}</span> /
                    ICAO:{" "}
                    <span className="font-semibold text-slate-700">{currentOperatingAirline.icao}</span>)
                  </p>
                  <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    ⚠️ La compagnie opératrice est une information du vol, pas du dossier. La modifier
                    s'applique{" "}
                    <span className="font-semibold">
                      {flightClaimsCount === undefined
                        ? "à tous les dossiers de ce vol"
                        : `aux ${flightClaimsCount} dossier(s) de ce vol`}
                    </span>
                    . Pour changer la société attaquée sur ce dossier uniquement, utilisez « Adversaire »
                    dans le résumé du dossier.
                  </div>
                  <div className="flex justify-end pt-3">
                    <Button
                      onClick={() => operatingAirlineId && updateOperatingAirline.mutate(operatingAirlineId)}
                      disabled={!operatingAirlineId || updateOperatingAirline.isPending}
                    >
                      Modifier la compagnie opératrice
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 py-6">
                    {SCHEDULE_FIELDS.map((field) => (
                      <div key={field.name}>
                        <Label
                          htmlFor={`schedule-${field.name}`}
                          className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700"
                        >
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {field.label}
                        </Label>
                        <DateTimePicker
                          id={`schedule-${field.name}`}
                          value={scheduleValues[field.name] ?? ""}
                          onChange={(value) =>
                            setScheduleValues((values) => ({ ...values, [field.name]: value }))
                          }
                        />
                        <p className="mt-1 text-xs text-gray-500">{field.description}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end py-4">
                    <Button onClick={submitSchedule} disabled={updateClaim.isPending}>
                      Modifier le vol effectif
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* FAMILY */}
            {claimFamilies ? (
              <AccordionItem value="families" className="border-none">
                <AccordionTrigger
                  className={`${sectionTriggerClass} bg-teal-100 text-teal-900 hover:bg-teal-200`}
                >
                  Modifier les familles
                </AccordionTrigger>
                <AccordionContent className="px-4 pt-4 pb-2 text-sm text-gray-500">
                  <div className="overflow-x-auto rounded-md border border-zinc-300 shadow-md">
                    <div className="flex py-3.5 text-sm font-semibold text-gray-900">
                      <div className="flex-1 pr-3 pl-4">Nom complet</div>
                      <div className="flex-1 px-3">Date de Naissance</div>
                      <div className="flex-1 px-3">Email</div>
                      <div className="w-44 px-3">Famille</div>
                      <div className="w-32 pr-4 pl-3" />
                    </div>
                    {displayFamilies.map((family) => (
                      <div key={family.id}>
                        <div className="border-t border-gray-200 bg-gray-50 py-2 pr-3 pl-4 text-left text-sm text-stone-600 italic">
                          Famille {family.familyName} ({family.usersClaims.length || "Aucun Passager"})
                        </div>
                        {family.usersClaims.map((userClaim, index) => (
                          <div
                            key={userClaim.id}
                            className={`flex items-center border-t py-4 text-sm ${
                              index === 0 ? "border-gray-300" : "border-gray-200"
                            }`}
                          >
                            <div className="flex-1 pr-3 pl-4 font-medium text-gray-900">
                              {userClaim.user.firstName} {userClaim.user.lastName?.toUpperCase()}
                            </div>
                            <div className="flex-1 px-3 text-gray-500">
                              {userClaim.user.birthdate ? String(userClaim.user.birthdate) : ""}
                            </div>
                            <div className="flex-1 px-3 text-gray-500">
                              {userClaim.email || userClaim.user.email}
                            </div>
                            {/* Family reassignment select — replaces the Vue drag-and-drop */}
                            <div className="w-44 px-3">
                              {displayFamilies.length > 1 ? (
                                <Select
                                  value={assignedFamilyId(userClaim.id) ?? family.id}
                                  onValueChange={(value) => {
                                    if (typeof value === "string") {
                                      setFamilyOverrides((overrides) => ({
                                        ...overrides,
                                        [userClaim.id]: value,
                                      }));
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-full" size="sm">
                                    <SelectValue>
                                      Famille{" "}
                                      {displayFamilies.find(
                                        (candidate) => candidate.id === assignedFamilyId(userClaim.id),
                                      )?.familyName ?? family.familyName}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {displayFamilies.map((candidate) => (
                                      <SelectItem key={candidate.id} value={candidate.id}>
                                        Famille {candidate.familyName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : null}
                            </div>
                            <div className="w-32 pr-4 pl-3 text-right font-medium">
                              {family.usersClaims.length > 1 ? (
                                <button
                                  type="button"
                                  className="text-indigo-600 hover:text-indigo-900"
                                  onClick={() => extractInNewFamily.mutate(userClaim.id)}
                                >
                                  Nouvelle famille
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  {hasFamilyChange ? (
                    <div className="flex justify-end py-4">
                      <Button onClick={submitFamilyChanges} disabled={updateClaimFamilies.isPending}>
                        Modifier les familles
                      </Button>
                    </div>
                  ) : null}
                </AccordionContent>
              </AccordionItem>
            ) : null}

            {/* SPLIT CLAIM (TECH-11) */}
            {showSplitSection ? (
              <AccordionItem value="split" className="border-none">
                <AccordionTrigger className={sectionTriggerClass}>Scinder le dossier</AccordionTrigger>
                <AccordionContent className="px-4 pt-4 pb-2 text-sm text-gray-500">
                  <p className="mb-3">
                    Sélectionnez les passagers à extraire dans un nouveau dossier (même PNR). Au moins un
                    passager doit rester sur le dossier d'origine.
                  </p>
                  <ul className="divide-y divide-gray-100">
                    {splitPassengers.map((userClaim) => (
                      <li key={userClaim.id} className="flex items-center gap-3 py-2">
                        <Checkbox
                          id={`split-${userClaim.id}`}
                          checked={selectedUserClaimIds.includes(userClaim.id)}
                          onCheckedChange={(checked) =>
                            setSelectedUserClaimIds((ids) =>
                              checked ? [...ids, userClaim.id] : ids.filter((id) => id !== userClaim.id),
                            )
                          }
                        />
                        <Label
                          htmlFor={`split-${userClaim.id}`}
                          className="text-sm font-medium text-gray-900"
                        >
                          {userClaim.user.firstName} {userClaim.user.lastName?.toUpperCase()}
                        </Label>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-end py-4">
                    <Button onClick={submitSplitClaim} disabled={!canSplit || splitClaim.isPending}>
                      Scinder le dossier ({selectedUserClaimIds.length})
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ) : null}

            {/* LEGAL */}
            <AccordionItem value="legal" className="border-none">
              <AccordionTrigger className={sectionTriggerClass}>Modifier la demande</AccordionTrigger>
              <AccordionContent className="px-4 pt-4 pb-2 text-sm text-gray-500">
                <div className="grid grid-cols-2 gap-4 py-2">
                  <div className="text-left">
                    <Label className="block text-sm font-medium text-gray-700">Type de dossier</Label>
                    <Select
                      value={claimTypeName ?? claim.claimType?.name ?? null}
                      onValueChange={(value) => setClaimTypeName(value as ClaimTypeName)}
                    >
                      <SelectTrigger className="mt-1 w-full">
                        <SelectValue>
                          {getClaimTypeDisplayName(claimTypeName ?? claim.claimType.name, { emoji: true })}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {CLAIM_TYPE_NAMES.map((name) => (
                          <SelectItem key={name} value={name}>
                            {getClaimTypeDisplayName(name, { emoji: true })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-left">
                    <Label className="block text-sm font-medium text-gray-700">
                      Montant règlement 261/2004
                    </Label>
                    <Select
                      value={
                        ec261CompensationAmount !== null
                          ? String(ec261CompensationAmount)
                          : String(claim.legalSpecification.ec261CompensationAmount ?? "")
                      }
                      onValueChange={(value) => setEc261CompensationAmount(value ? Number(value) : null)}
                    >
                      <SelectTrigger className="mt-1 w-full">
                        <SelectValue>
                          {`${ec261CompensationAmount ?? claim.legalSpecification.ec261CompensationAmount ?? 0} €`}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {COMPENSATION_OPTIONS.map((amount) => (
                          <SelectItem key={amount} value={String(amount)}>
                            {amount} €
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-6 text-left">
                  <h3 className="text-base leading-7 font-medium text-gray-900">
                    Modifier les demandes individuelles :
                  </h3>
                  <div className="grid grid-cols-2 gap-4 py-6 lg:grid-cols-4">
                    {claim.usersClaims.map((userClaim) => (
                      <div key={userClaim.id}>
                        <p className="text-sm font-medium text-gray-900">
                          {userClaim.user.firstName} {userClaim.user.lastName?.toUpperCase()}
                        </p>
                        <Label
                          htmlFor={`mc99-${userClaim.id}`}
                          className="mt-1 block text-sm font-medium text-gray-700"
                        >
                          Montant Convention de Montréal :
                        </Label>
                        <div className="relative mt-2 rounded-md shadow-sm">
                          <input
                            id={`mc99-${userClaim.id}`}
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Entrer le montant"
                            className="block w-full rounded-md border-0 py-1.5 pr-20 pl-3 text-sm text-gray-900 ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-blue-600 focus:ring-inset"
                            value={mc99Amounts[userClaim.id]?.amount ?? ""}
                            onChange={(event) =>
                              setMc99Amounts((amounts) => ({
                                ...amounts,
                                [userClaim.id]: {
                                  amount: event.target.value,
                                  currency: amounts[userClaim.id]?.currency ?? "EUR",
                                },
                              }))
                            }
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center">
                            <select
                              aria-label="Devise"
                              className="h-full rounded-md border-0 bg-transparent py-0 pr-7 pl-2 text-gray-500 focus:ring-2 focus:ring-blue-600 focus:ring-inset"
                              value={mc99Amounts[userClaim.id]?.currency ?? "EUR"}
                              onChange={(event) =>
                                setMc99Amounts((amounts) => ({
                                  ...amounts,
                                  [userClaim.id]: {
                                    amount: amounts[userClaim.id]?.amount ?? "",
                                    currency: event.target.value,
                                  },
                                }))
                              }
                            >
                              {PRICE_CURRENCIES.map((currency) => (
                                <option key={currency} value={currency}>
                                  {currency}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end py-4">
                  <Button onClick={submitLegalSpecification} disabled={updateClaim.isPending}>
                    Modifier la demande
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* TENANT SELECTION */}
            {showTenantSection ? (
              <AccordionItem value="tenant" className="border-none">
                <AccordionTrigger className={sectionTriggerClass}>
                  Changer le tenant ou l'office ID
                </AccordionTrigger>
                <AccordionContent className="px-4 pt-4 pb-2 text-sm text-gray-500">
                  <div className="py-2">
                    <p className="text-sm text-gray-600">
                      Tenant actuel:{" "}
                      <span className="font-semibold">{claim.tenantAssociation?.sourceTenant?.name}</span>
                    </p>
                    <p className="mb-4 text-sm text-gray-600">
                      Office ID actuel: <span className="font-semibold">{officeLabel(currentOffice)}</span>
                    </p>
                    <Alert className="mb-4">
                      <TriangleAlert className="text-amber-500" />
                      <AlertTitle>Attention</AlertTitle>
                      <AlertDescription>
                        <ul className="space-y-1">
                          <li className="text-sm text-amber-600">
                            Mettre à jour l'agence de voyage de la réservation
                          </li>
                          <li className="text-sm text-amber-600">
                            Recalculer les commissions selon le contrat du nouveau tenant
                          </li>
                          <li className="text-sm text-amber-600">
                            Réinitialiser l'agent assigné (changement de tenant uniquement)
                          </li>
                          <li className="text-sm text-amber-600">
                            Seuls les agents de l'office ID choisi verront le dossier, en plus de l'agent
                            assigné
                          </li>
                          <li className="text-sm text-amber-600">
                            Les signatures en cours ne seront pas annulées
                          </li>
                        </ul>
                      </AlertDescription>
                    </Alert>
                    {tenants.length > 0 ? (
                      <div className="text-left">
                        <Label className="block text-sm font-medium text-gray-700">Nouveau tenant</Label>
                        <Select
                          value={selectedTenantId ?? currentTenantId}
                          onValueChange={(value) => {
                            setSelectedTenantId(value);
                            // Offices belong to a tenant: the previous pick does not exist there
                            setSelectedOfficeId(null);
                          }}
                        >
                          <SelectTrigger className="mt-1 w-full">
                            <SelectValue>
                              {tenants.find((tenant) => tenant.id === (selectedTenantId ?? currentTenantId))
                                ?.name ?? "Sélectionner un tenant"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {tenants.map((tenant) => (
                              <SelectItem key={tenant.id} value={tenant.id}>
                                {tenant.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="mt-1 text-xs text-gray-500">
                          Sélectionnez le tenant vers lequel transférer cette réclamation
                        </p>
                      </div>
                    ) : null}
                    <div className="mt-4 text-left">
                      <Label className="mb-1 block text-sm font-medium text-gray-700">Office ID</Label>
                      <OfficeCombobox
                        offices={offices}
                        value={targetOfficeId}
                        onChange={setSelectedOfficeId}
                        placeholder={isTenantChanged ? "Office passif du tenant" : "Sélectionner un office"}
                        disabled={offices.length === 0}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Office ID de la réservation : détermine les agents qui voient le dossier
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end py-4">
                    <Button onClick={submitTenantChange} disabled={changeClaimTenant.isPending}>
                      Changer le tenant / l'office ID
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ) : null}
          </Accordion>
        </DialogPanel>
      </DialogContent>
    </Dialog>
  );
}

interface SegmentSelectProps {
  label: string;
  description: string;
  options: Array<{ label: string; value: number }>;
  value: number | null;
  onChange: (segmentId: number) => void;
}

function SegmentSelect({ label, description, options, value, onChange }: SegmentSelectProps) {
  const selected = options.find((option) => option.value === value) ?? options[0];

  return (
    <div className="text-left">
      <Label className="block text-sm font-medium text-gray-700">{label}</Label>
      <Select
        value={selected ? String(selected.value) : null}
        onValueChange={(next) => {
          if (next) {
            onChange(Number(next));
          }
        }}
      >
        <SelectTrigger className="mt-1 w-full">
          <SelectValue>{selected?.label ?? "Sélectionner un segment"}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-1 text-xs text-gray-500">{description}</p>
    </div>
  );
}
